import React, { useState, createRef, useEffect } from 'react'

import {
    Box,
    Button
} from '@mui/material';

import { useNavigate, Link } from 'react-router-dom';

import DateFilter from '@inovua/reactdatagrid-community/DateFilter'

import { CommonIcons, icons } from '../../common/CommonIcons';
import CommonBreadcumbs from '../../common/CommonBreadcumbs';
import DataGridToolbar from '../../common/DataGridToolbar';
import DataGridTable from '../../common/DataGridTable';
import CommonRestricted from '../../common/CommonRestricted';

import DataGridRowActionMenu from '../../common/DataGridRowActionMenu';

import { useSystemPrompt } from '../../../hooks/useSystemPrompt';
import { useUsers, userTypes, targetTypes } from "../../../hooks/api/useUsers";
import { useDataGridParams } from '../../../hooks/useDataGridParams';
import { useSystemRoutes, routeTypes } from '../../../hooks/useSystemRoutes';
import { useSystemHead, headTitles } from '../../../hooks/useSystemHead';
import { usePermissions, scopeIds, scopeTypes, functionTypes } from '../../../hooks/usePermissions';
import { useTranslations } from '../../../hooks/useTranslations';

export default function ListRetailers({
    routeType
}) {
    const { t } = useTranslations({ ns: ["common", "@user"] })

    const navigate = useNavigate();
    const { promptMessage } = useSystemPrompt();
    const { list: get, remove } = useUsers({ userType: userTypes.retail });

    const { defineColumns, columns, filterValues, getRenderColumnLink, getRenderColumnBooleanDisplay, getRenderColumnTextDisplay } = useDataGridParams('id')
    const { isPermissionAllow } = usePermissions(routeType, scopeIds.user.id, scopeTypes.user.retail.id)

    const [selectedIds, setSelectedIds] = useState([])
    const [refreshDataGrid, setRefreshDataGrid] = useState(0)
    const { getRoute } = useSystemRoutes(routeType);
    const { getHtmlHead } = useSystemHead(routeType)

    const gridRef = createRef();

    const resetDataGridTableSelection = () => {
        setSelectedIds([])
    }

    const fetchData = async (props) => {

        resetDataGridTableSelection();

        try {

            // props.dataTypes = ["branch_user_count", "branch_count", "member_count"]
            props.dataTypes = ["branch_user_count", "branch_count"]

            const result = await get(props)

            return {
                data: result?.data?.retailers,
                count: result?.data?.total
            }

        } catch (error) {

            promptMessage(false, `Error getting results (${error?.message})`)

            return {
                data: [],
                count: 0
            }
        }
    }

    const filterValue =
        [
            // { name: 'action', operator: 'eq', type: 'string', value: '' },
            { name: 'company_name', operator: 'eq', type: 'string', value: '' },
            { name: 'remark', operator: 'eq', type: 'string', value: '' },
            { name: 'reg_no', operator: 'eq', type: 'string', value: '' },
            { name: 'code', operator: 'eq', type: 'string', value: '' },
            //   { name: 'ax_createdatetime', operator: 'eq', type: 'string', value: '' },
        ];

    useEffect(() => {

        defineColumnsView()

    }, [])

    const defineColumnsView = () => {
        defineColumns([
            {
                name: 'company_name',
                header: t('company'),
                renderValue: ({ value, cellProps }) => {

                    if (isPermissionAllow({ functionType: functionTypes.pageView.id }) === false) {
                        return getRenderColumnTextDisplay(value)
                    }

                    let path = getRoute(`users/retailer/view/${cellProps.data.user_id}`);

                    return getRenderColumnLink(path, value)

                }
            },
            {
                name: 'reg_no', header: t('regNo')
            },
            {
                name: 'code', header: t('code')
            },
            {
                name: 'username', header: t('username')
            },
            {
                name: 'status',
                header: t('status'),
                renderValue: ({ cellProps, value }) => getRenderColumnBooleanDisplay(value, [t("enabled"), t("disabled")], ["primary", "default"], "")
            },
            {
                name: 'branch_count',
                header: t('outlets'),
                sortable: false,
                renderValue: ({ value, cellProps }) => {

                    let displayValue = parseInt(value, 10) > 0 ? `${value} ${t('outlet')}` : ``

                    return (
                        <>{displayValue}</>
                    )

                }
            },
            {
                name: 'branch_user_count',
                header: t('users'),
                sortable: false,
                renderValue: ({ value, cellProps }) => {

                    let displayValue = parseInt(value, 10) > 0 ? `${value} ${t('user')}` : ``

                    return (
                        <>{displayValue}</>
                    )

                }
            },
            // {
            //     name: 'member_count',
            //     header: t('members'),
            //     sortable: false,
            //     renderValue: ({ value, cellProps }) => {

            //         let displayValue = parseInt(value, 10) > 0 ? `${value} ${t('member')}` : ``

            //         return (
            //             <>{displayValue}</>
            //         )

            //     }
            // },
        ])
    }

    const handleDelete = async () => {

        try {

            let result = await remove({ ids: selectedIds, targetType: targetTypes.retail })

            promptMessage(true, result?.message)
            setRefreshDataGrid(val => val + 1)

        } catch (error) {
            promptMessage(false, error?.message)
        }

    }

    const RenderToolbar = () => {

        const getToolbarMainMenus = () => {

            let props = [];

            if (isPermissionAllow({ functionType: functionTypes.pageNew.id })) {
                props.push({
                    displayIconName: icons.plus,
                    onClick: () => {
                        navigate(getRoute("users/retailer/new"));
                    }
                })
            }

            return props
        }

        const getActionOnClick = () => {

            let handleFunction = null

            if (isPermissionAllow({ functionType: functionTypes.remove.id })) {
                handleFunction = handleDelete
            }

            return handleFunction
        }

        return (
            <DataGridToolbar
                selectedRowCount={
                    selectedIds.length
                }
                mainMenus={
                    getToolbarMainMenus()
                }
                actionOnClickRemove={
                    getActionOnClick()
                }
                actionMenus={[]}
            />
        )
    }

    const RenderDataGridTable = () => {

        return (
            <DataGridTable
                ref={gridRef}
                refresh={refreshDataGrid}
                fetchData={fetchData}
                dataGrid={{
                    idProperty: "user_id",
                    style: {
                        minHeight: 600
                    },
                    pageSizes: [20, 50, 100],
                    defaultLimit: 10,
                    defaultSkip: 0,
                    checkboxColumn:
                        isPermissionAllow({ functionType: functionTypes.remove.id }),
                    columns,
                    defaultFilterValue: filterValues,
                    onSelectionChange: (items) => {
                        setSelectedIds(
                            items
                        )
                    }
                }}

            />
        )
    }

    return (
        <Box>
            {
                getHtmlHead({ routeType, title: headTitles.retailers })
            }

            <CommonBreadcumbs
                routeType={routeType}
                pageTitle={t("retailers")}
                breadcrumbs={[
                    {
                        displayText: t("retailers"),
                        props: {
                            // underline: "hover",
                            // href: "/"
                        }
                    },
                ]}
            />

            {
                isPermissionAllow({ functionType: functionTypes.pageList.id }) ?
                    <>
                        {
                            RenderToolbar()
                        }
                        {
                            RenderDataGridTable()
                        }
                    </>
                    :
                    (
                        <CommonRestricted />
                    )
            }

        </Box>
    )
}
